/**
 * identityService.ts
 *
 * Kernel-Authoritative Identity & Principal Session Service.
 * Manages bootstrap lifecycle, real authentication sessions, worker teardowns,
 * and role-preview simulation sandboxes.
 */

import {
  type Principal,
  type Role,
  type Capability,
  type SessionMode,
  type AuthorizationContext,
  ROLE_CAPABILITY_MAP,
} from '../../kernel/authorization/capabilities.js';

export type GenesisState = 'UNINITIALIZED' | 'BOOTSTRAPPING' | 'INITIALIZED';

export interface StoredIdentityRecord {
  readonly principalId: string;
  readonly displayName: string;
  readonly role: Role;
  readonly deviceOwnership: 'host-owned' | 'user-owned' | 'managed';
  readonly createdAt: number;
}

export class IdentityService {
  private genesisState: GenesisState = 'INITIALIZED'; // Default initialized in normal run
  private activePrincipal: Principal;
  private activeRoles: Role[] = ['sovereign-owner'];
  private activeSessionMode: SessionMode;
  private knownPrincipals: Map<string, StoredIdentityRecord> = new Map();
  private bootstrapSecret: string | null = null;
  private listeners: Set<(ctx: AuthorizationContext) => void> = new Set();

  constructor() {
    const isElectron = typeof window !== 'undefined' && Boolean((window as any).electronAPI);
    const defaultPrincipal: Principal = isElectron
      ? {
          id: 'sovereign-owner',
          displayName: 'Sovereign Owner',
          authenticated: true,
          sessionId: `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          deviceOwnership: 'host-owned',
        }
      : {
          id: 'member-peer',
          displayName: 'Peer Member',
          authenticated: true,
          sessionId: `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          deviceOwnership: 'user-owned',
        };

    this.activePrincipal = defaultPrincipal;
    this.activeRoles = isElectron ? ['sovereign-owner'] : ['member'];
    this.activeSessionMode = { type: 'authenticated', principalId: defaultPrincipal.id };

    // Register built-in sample principals
    this.knownPrincipals.set('sovereign-owner', {
      principalId: 'sovereign-owner',
      displayName: 'Sovereign Owner (Genesis)',
      role: 'sovereign-owner',
      deviceOwnership: 'host-owned',
      createdAt: Date.now(),
    });
    this.knownPrincipals.set('node-admin', {
      principalId: 'node-admin',
      displayName: 'Node Administrator',
      role: 'node-admin',
      deviceOwnership: 'host-owned',
      createdAt: Date.now(),
    });
    this.knownPrincipals.set('member-alice', {
      principalId: 'member-alice',
      displayName: 'Alice (Cashier / Member)',
      role: 'member',
      deviceOwnership: 'user-owned',
      createdAt: Date.now(),
    });

    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('mesh_active_principal_id');
      if (stored && this.knownPrincipals.has(stored)) {
        const p = this.knownPrincipals.get(stored)!;
        this.activePrincipal = {
          id: p.principalId,
          displayName: p.displayName,
          authenticated: true,
          sessionId: `sess_${Date.now()}`,
          deviceOwnership: p.deviceOwnership,
        };
        this.activeRoles = [p.role];
        this.activeSessionMode = { type: 'authenticated', principalId: p.principalId };
      }
    }
  }

  getGenesisState(): GenesisState {
    return this.genesisState;
  }

  /**
   * One-time Genesis Bootstrap Endpoint.
   * Fails if already initialized or secret mismatch.
   */
  async executeGenesisBootstrap(
    ownerPrincipalId: string,
    displayName: string,
    setupSecret: string
  ): Promise<AuthorizationContext> {
    if (this.genesisState === 'INITIALIZED') {
      throw new Error('[IdentityService] E_ALREADY_INITIALIZED: Genesis bootstrap has already been performed.');
    }
    if (this.bootstrapSecret && setupSecret !== this.bootstrapSecret) {
      throw new Error('[IdentityService] E_INVALID_BOOTSTRAP_SECRET: Provided setup secret is invalid.');
    }

    this.genesisState = 'INITIALIZED';
    this.bootstrapSecret = null; // Invalidate secret immediately

    const record: StoredIdentityRecord = {
      principalId: ownerPrincipalId,
      displayName,
      role: 'sovereign-owner',
      deviceOwnership: 'host-owned',
      createdAt: Date.now(),
    };
    this.knownPrincipals.set(ownerPrincipalId, record);

    this.activePrincipal = {
      id: ownerPrincipalId,
      displayName,
      authenticated: true,
      sessionId: `sess_genesis_${Date.now()}`,
      deviceOwnership: 'host-owned',
    };
    this.activeRoles = ['sovereign-owner'];
    this.activeSessionMode = { type: 'authenticated', principalId: ownerPrincipalId };

    if (typeof window !== 'undefined') {
      localStorage.setItem('sovereign_owner_id', ownerPrincipalId);
      localStorage.setItem('mesh_active_principal_id', ownerPrincipalId);
    }

    const ctx = this.getAuthorizationContext();
    this.notify(ctx);
    return ctx;
  }

  /**
   * Authenticate / Switch to a real principal.
   */
  async loginAsPrincipal(principalId: string): Promise<AuthorizationContext> {
    const record = this.knownPrincipals.get(principalId);
    if (!record) {
      throw new Error(`[IdentityService] Unknown principal ID: ${principalId}`);
    }

    this.activePrincipal = {
      id: record.principalId,
      displayName: record.displayName,
      authenticated: true,
      sessionId: `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      deviceOwnership: record.deviceOwnership,
    };
    this.activeRoles = [record.role];
    this.activeSessionMode = { type: 'authenticated', principalId: record.principalId };

    if (typeof window !== 'undefined') {
      localStorage.setItem('mesh_active_principal_id', record.principalId);
      if (record.role === 'sovereign-owner') {
        localStorage.setItem('sovereign_owner_id', record.principalId);
      }
    }

    const ctx = this.getAuthorizationContext();
    this.notify(ctx);
    return ctx;
  }

  /**
   * Enter role-preview simulation mode (strictly read-only).
   */
  enterRolePreview(previewRole: Role): AuthorizationContext {
    this.activeSessionMode = {
      type: 'role-preview',
      previewRole,
      sourcePrincipalId: this.activePrincipal.id,
    };
    this.activeRoles = [previewRole];

    const ctx = this.getAuthorizationContext();
    this.notify(ctx);
    return ctx;
  }

  /**
   * Exit role-preview simulation mode and restore authenticated session.
   */
  exitRolePreview(): AuthorizationContext {
    const record = this.knownPrincipals.get(this.activePrincipal.id);
    const restoredRole = record ? record.role : 'sovereign-owner';
    this.activeRoles = [restoredRole];
    this.activeSessionMode = { type: 'authenticated', principalId: this.activePrincipal.id };

    const ctx = this.getAuthorizationContext();
    this.notify(ctx);
    return ctx;
  }

  /**
   * Clean Logout: Revokes session token, clears sensitive caches,
   * emits session:revoked event for worker cleanup and plugin deactivation.
   */
  async logout(): Promise<void> {
    const revokedSessionId = this.activePrincipal.sessionId;
    this.activePrincipal = {
      id: 'unauthenticated-guest',
      displayName: 'Guest',
      authenticated: false,
      sessionId: '',
      deviceOwnership: 'user-owned',
    };
    this.activeRoles = ['member'];
    this.activeSessionMode = { type: 'authenticated', principalId: 'unauthenticated-guest' };

    if (typeof window !== 'undefined') {
      localStorage.removeItem('mesh_active_principal_id');
      window.dispatchEvent(
        new CustomEvent('mesh:session:revoked', { detail: { sessionId: revokedSessionId } })
      );
    }

    const ctx = this.getAuthorizationContext();
    this.notify(ctx);
  }

  getAuthorizationContext(): AuthorizationContext {
    const caps = new Set<Capability>();
    for (const role of this.activeRoles) {
      const granted = ROLE_CAPABILITY_MAP[role] || [];
      for (const c of granted) {
        caps.add(c);
      }
    }

    return {
      principal: this.activePrincipal,
      roles: this.activeRoles,
      capabilities: Array.from(caps),
      sessionMode: this.activeSessionMode,
    };
  }

  getKnownPrincipals(): readonly StoredIdentityRecord[] {
    return Array.from(this.knownPrincipals.values());
  }

  subscribe(listener: (ctx: AuthorizationContext) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(ctx: AuthorizationContext): void {
    for (const listener of this.listeners) {
      try {
        listener(ctx);
      } catch (err) {
        console.error('[IdentityService] Listener error:', err);
      }
    }
  }
}

export const globalIdentityService = new IdentityService();
